import { Linkedin, Youtube, Facebook } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SOCIALS, type SocialKey } from '@/lib/brand';
import TelegramIcon from './TelegramIcon';

const DEFAULT_ORDER: SocialKey[] = ['telegram', 'linkedin', 'youtube', 'facebook'];

/** Per-channel icon component. Centralized so call-sites never map keys to icons themselves. */
type IconProps = { className?: string; size?: number };
const ICON_MAP: Record<SocialKey, React.ComponentType<IconProps>> = {
    telegram: TelegramIcon,
    linkedin: Linkedin,
    youtube: Youtube,
    facebook: Facebook,
};

type Variant = 'icon' | 'pill' | 'cta';

interface Props {
    channels?: SocialKey[];
    variant: Variant;
    /** Icon size in px (icon variant only). Default 14. */
    iconSize?: number;
    /** Size of the square button (icon variant only). Default 28. */
    buttonSize?: number;
    className?: string;
}

/**
 * Renders a row of social-channel links.
 *
 * - `variant="icon"`: small neutral square, used in footer social row and chat-widget strip.
 * - `variant="pill"`: short pill with brand background, used in thank-you screen secondary actions.
 * - `variant="cta"`: full-width gradient button with brand colors, used for primary follow CTAs.
 *
 * Brand colors are read from `SOCIALS[key].brand` — no hex literals at the call-site.
 */
const SocialRow = ({
    channels = DEFAULT_ORDER,
    variant,
    iconSize = 14,
    buttonSize = 28,
    className,
}: Props) => {
    if (variant === 'icon') {
        return (
            <div className={cn('flex gap-1.5', className)}>
                {channels.map((key) => {
                    const entry = SOCIALS[key];
                    const Icon = ICON_MAP[key];
                    return (
                        <a
                            key={key}
                            href={entry.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={entry.labelEn}
                            title={entry.labelAr}
                            style={{ width: buttonSize, height: buttonSize }}
                            className="rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-muted-foreground hover:text-foreground transition-all"
                        >
                            <Icon size={iconSize} />
                        </a>
                    );
                })}
            </div>
        );
    }

    if (variant === 'pill') {
        return (
            <div className={cn('grid grid-cols-2 gap-2', className)}>
                {channels.map((key) => {
                    const entry = SOCIALS[key];
                    const Icon = ICON_MAP[key];
                    return (
                        <a
                            key={key}
                            href={entry.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ backgroundColor: entry.brand }}
                            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-white font-bold text-xs hover:opacity-90 transition-all"
                        >
                            <Icon className="w-4 h-4" />
                            {entry.labelEn}
                        </a>
                    );
                })}
            </div>
        );
    }

    // variant === 'cta'
    return (
        <div className={cn('space-y-3', className)}>
            {channels.map((key) => {
                const entry = SOCIALS[key];
                const Icon = ICON_MAP[key];
                return (
                    <a
                        key={key}
                        href={entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: entry.brand }}
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-white font-bold text-sm hover:opacity-90 hover:shadow-lg transition-all"
                    >
                        <Icon className="w-4 h-4" />
                        {entry.labelAr}
                    </a>
                );
            })}
        </div>
    );
};

export default SocialRow;
